<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';

  import { FormGroup, InlineNotification, Modal } from 'carbon-components-svelte';
  import { Form } from '@mst-fe/sveltejs-forms';

  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import SlaTaskPreview from './SlaTaskPreview.svelte';
  import SlaTaskFormItems from './SlaTaskFormItems.svelte';
  import SlaAssociationsFormItems from './SlaAssociationsFormItems.svelte';
  import Wizard from '../../../components/wizard/Wizard.svelte';
  import WizardStep from '../../../components/wizard/WizardStep.svelte';

  import {
    buildSlaTaskSchema,
    defaultSlaTaskInitialValues,
    getSlaTaskSubmitHandler,
    loadSlaTaskReferenceData,
    SlaTaskFormVariants,
    type ExistingSchedule,
  } from './sla-task-form-shared';

  import { getServerErrorMessage } from '../../../utils';

  import type { DataFeedInventorySummaryRow } from '../../../services';
  import type { SlaTaskRow } from '../../../../shared/types/sla-task-types';

  export let open = false;

  const dispatch = createEventDispatcher<{ close: { action: string; data: SlaTaskRow } | undefined }>();

  const WIZARD_STEPS = [{ label: 'Task Details' }, { label: 'Associations' }, { label: 'Review' }];

  const initialValues = { ...defaultSlaTaskInitialValues };

  let loading = false,
    dataLoading = false,
    availableFeeds: DataFeedInventorySummaryRow[] = [],
    existingSchedules: ExistingSchedule[] = [],
    resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null;

  async function loadReferenceData() {
    dataLoading = true;

    try {
      const referenceData = await loadSlaTaskReferenceData({ includeFeeds: true, includeSchedules: true });

      availableFeeds = referenceData.availableFeeds;
      existingSchedules = referenceData.existingSchedules;
    } catch (error) {
      console.error('[AddSlaTaskModal] Failed to load reference data!', error);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load reference data. Please try again later.',
      };
    } finally {
      dataLoading = false;
    }
  }

  async function handleFormSubmission(event: CustomEvent) {
    const { values } = event.detail;

    resultNotification = null;
    loading = true;

    try {
      const { action, submit } = getSlaTaskSubmitHandler(SlaTaskFormVariants.ADD);
      const savedTask = await submit(values);

      dispatch('close', { action, data: savedTask });
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Verify your submission and try again.';
      console.error('[AddSlaTaskModal] Failed to submit!', error);

      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to submit! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function onModalClose() {
    dispatch('close');
  }

  onMount(loadReferenceData);

  $: schema = buildSlaTaskSchema({ includeTaskFields: true, includeAssociations: true, existingSchedules });
</script>

<Modal
  bind:open
  modalHeading="Add SLA Task"
  passiveModal
  preventCloseOnClickOutside
  size="lg"
  shouldSubmitOnEnter={false}
  on:close={onModalClose}
>
  {#if resultNotification}
    <InlineNotification
      kind={resultNotification.kind}
      lowContrast
      title={resultNotification.title}
      subtitle={resultNotification.subtitle}
      hideCloseButton
    />
  {/if}

  <LoadingSpinner loading={dataLoading}>
    <Form
      fieldDomString="input,select,textarea,div[name]"
      {initialValues}
      {schema}
      let:submitForm
      let:errors
      let:setValue
      let:touched
      let:values
      on:submit={handleFormSubmission}
    >
      <Wizard
        {loading}
        steps={WIZARD_STEPS}
        finalStepButtonLabel="Create"
        confirmationMessage="I confirm the selections are correct and I am ready to create the SLA task"
        onFinish={submitForm}
      >
        <WizardStep nextButtonActive={!errors.friendlyName && !errors.time && !errors.timezone}>
          <FormGroup>
            <SlaTaskFormItems {errors} {touched} {values} {setValue} disabled={loading} {existingSchedules} />
          </FormGroup>
        </WizardStep>

        <WizardStep nextButtonActive={Array.isArray(values.associations) && values.associations.length > 0}>
          <SlaAssociationsFormItems {errors} {values} {setValue} disabled={loading} {availableFeeds} />
        </WizardStep>

        <WizardStep>
          <SlaTaskPreview {values} {availableFeeds} />
        </WizardStep>
      </Wizard>
    </Form>
  </LoadingSpinner>
</Modal>
